// mattermost-chat-command-reply.ts — backlog M1/M2 (2026-10-06): a chat
// command that is NOT turned into an inbox directive still deserves an
// answer in the mission thread, otherwise the operator sees silence.
//
// Delivery: the reply is RECORDED as a `mattermost_chat_reply` outbox op
// (with the payload needed to send it); the transport tick drains the outbox
// again right after reading chat (supervisor-transport-tick.ts), and the
// production `outboxSend` hands this kind to `deliverChatReplyOperation`
// below instead of `deliverRootPostIntent`. Re-delivery of the same chat post
// (poll overlap) is deduped by the event id derived from the source post id.
//
// Best effort, by decision (review 261006 M4): there is no correlation-marker
// lookup on the production sender, so after an ambiguous failure (5xx /
// network) the server may already hold the post and the next attempt can
// post a DUPLICATE reply — accepted for a short informational reply. What is
// NOT accepted is resending forever: after MAX_CHAT_REPLY_ATTEMPTS failures
// (e.g. a permanent 4xx — root post deleted, bot removed) the op is marked
// `failed`, a terminal status the outbox never lists again.
import { sha256Checksum } from "./canonical-json-and-checksum.js";
import {
  readOutboxOperation,
  recordOutboxIntent,
  saveOutboxOperation,
  updateOutboxStatus,
} from "./delivery-outbox.js";
import { isProvenPreSendError } from "./outbox-delivery-worker.js";

export const CHAT_REPLY_OUTBOX_KIND = "mattermost_chat_reply";
const MAX_CHAT_REPLY_ATTEMPTS = 3;

/** Mirrors the CLI refusal of `autopilot answer <ticket> --request <id>`. */
export const LEGACY_CHAT_ANSWER_REPLY =
  "`@avatar answer <ticket> <request>: …` đã bỏ — controller chỉ nhận câu trả lời interview. " +
  "Dùng: `@avatar answer <question-id>: <giá trị>` " +
  '(câu hỏi needs_decision có id `<ticket>-needs-decision-<8 hex>` — id đầy đủ in ở dòng "Dispatch bị chặn" của `avatar autopilot run` và trong `interview.json` của mission; giá trị = đúng một chuỗi trong `options` của câu trong interview.json (nhãn, không phải id worker trả), câu `text` thì gõ tự do).';

export function chatStatusReply(missionId: string): string {
  return (
    "`@avatar status` chưa hỗ trợ trả lời trong chat — " +
    `chạy \`avatar autopilot status --mission ${missionId}\` trên máy đang chạy mission.`
  );
}

export interface EnqueueChatCommandReplyInput {
  controlRoot: string;
  missionId: string;
  channelId: string;
  rootId: string;
  sourcePostId: string;
  /** Orders the reply among pending ops (the source post's create_at). */
  sequence: number;
  message: string;
  now?: () => Date;
}

/** Records one thread reply per source post; `false` = already recorded (no-op). */
export async function enqueueChatCommandReply(
  input: EnqueueChatCommandReplyInput,
): Promise<boolean> {
  const eventId = `chat-reply-${input.sourcePostId}`;
  if ((await readOutboxOperation(input.controlRoot, eventId)) !== undefined) return false;
  await recordOutboxIntent({
    controlRoot: input.controlRoot,
    eventId,
    missionId: input.missionId,
    sequence: input.sequence,
    kind: CHAT_REPLY_OUTBOX_KIND,
    payloadHash: sha256Checksum(input.message),
    payload: { channel_id: input.channelId, message: input.message, root_id: input.rootId },
    now: input.now,
  });
  return true;
}

export interface DeliverChatReplyInput {
  controlRoot: string;
  eventId: string;
  createPost: (channelId: string, message: string, rootId?: string) => Promise<{ id: string }>;
  now?: () => Date;
}

/**
 * One delivery attempt for a recorded chat reply. Counts the attempt BEFORE
 * the POST (a crash mid-POST still counts), throws on failure so the outbox
 * worker leaves the op alone, and marks it `failed` on the last allowed one.
 * A failure PROVEN not sent (429 / preSendSafe — the worker retries those
 * densely within one tick) is not an attempt: the count is rolled back (L8).
 * Only ops of this kind are accepted (L11 — never a root `mattermost_post`).
 */
export async function deliverChatReplyOperation(
  input: DeliverChatReplyInput,
): Promise<{ remoteId: string }> {
  const op = await readOutboxOperation(input.controlRoot, input.eventId);
  if (op === undefined || op.kind !== CHAT_REPLY_OUTBOX_KIND || op.payload === undefined) {
    throw new Error(
      `chat reply "${input.eventId}": không phải op ${CHAT_REPLY_OUTBOX_KIND} gửi được.`,
    );
  }
  if (op.status === "failed" || op.status === "sent") {
    throw new Error(`chat reply "${input.eventId}" không còn gửi được (${op.status}).`);
  }
  const attempts = (op.attempts ?? 0) + 1;
  await saveOutboxOperation(input.controlRoot, { ...op, attempts });
  try {
    const post = await input.createPost(
      op.payload.channel_id,
      op.payload.message,
      op.payload.root_id,
    );
    return { remoteId: post.id };
  } catch (err) {
    if (isProvenPreSendError(err)) {
      await saveOutboxOperation(input.controlRoot, op); // not an attempt: roll the count back
      throw err;
    }
    if (attempts >= MAX_CHAT_REPLY_ATTEMPTS) {
      await updateOutboxStatus(input.controlRoot, input.eventId, "failed", undefined, input.now);
    }
    throw err;
  }
}
